<template>
    <div class="main-content-area" v-touch:swipe.right="backButton">
        <div class="main-content-toolbar">
            <v-toolbar>
                <v-btn size="x-large" variant="text" @click="backButton">
                    <v-icon left>mdi-arrow-left</v-icon>
                    {{ $t('goBack') }}
                </v-btn>
            </v-toolbar>
            <v-toolbar>
                <v-toolbar-title class="text-center">
                    <div class="toolbar-title">
                        <h3>{{ $t('truckerGate') }}</h3>
                        <span class="job-status">{{ $t('gateOut') }}</span>
                    </div>
                </v-toolbar-title>
            </v-toolbar>
            <v-toolbar>
                <v-icon class="btn-icon" color="red" style="transform: scaleX(-1)">
                    mdi-truck-delivery
                </v-icon>
            </v-toolbar>
        </div>

        <div class="center-flex" v-if="!ready">
            <DataLoading :type="4" />
        </div>

        <div v-if="ready" class="main-content-playground">
            <!-- First Row Buttons -->
            <div class="gate-out">
                <v-row>
                    <!-- Reference Button -->
                    <v-col cols="12" md="4" class="rowBtnCol center">
                        <v-btn color="primary" size="x-large" width="100%" height="100%" class="white--text"
                            :disabled="false" @click="selectReferenceButton">
                            <div class="button-content">
                                <v-icon large>{{ referenceIcon }}</v-icon>
                                <span class="container-btn-text">{{ $t('reference') }}</span>
                                <span>{{ selectedReference || $t('selectReference') }}</span>
                            </div>
                        </v-btn>
                    </v-col>

                    <!-- Container Number Button -->
                    <v-col cols="12" md="4" class="rowBtnCol center">
                        <v-btn color="primary" size="x-large" width="100%" height="100%" class="white--text"
                            :disabled="isContainerButtonDisabled" @click="selectContainerButton">
                            <div class="button-content">
                                <v-icon large>{{ containerIcon }}</v-icon>
                                <span class="container-btn-text">{{ $t('containerNumber') }}</span>
                                <span>{{ selectedContainerNumber || $t('enterContainerNumber') }}</span>
                            </div>
                        </v-btn>
                    </v-col>

                    <!-- ISO Code Button (Always Disabled) -->
                    <v-col cols="12" md="4" class="rowBtnCol center">
                        <v-btn color="primary" size="x-large" width="100%" height="100%" class="white--text"
                            :disabled="true">
                            <div class="button-content">
                                <v-icon large>{{ isoCodeIcon }}</v-icon>
                                <span class="container-btn-text">{{ $t('isoCode') }}</span>
                                <span>{{ selectedIsoCode || $t('selectIsoCode') }}</span>
                            </div>
                        </v-btn>
                    </v-col>
                </v-row>
            </div>

            <div>&nbsp;</div>

            <!-- Second Row Buttons -->
            <div class="gate-out">
                <v-row>
                    <!-- Customer / Operator Button (Always Disabled) -->
                    <v-col cols="12" md="4" class="rowBtnCol center">
                        <v-btn color="primary" size="x-large" width="100%" height="100%" class="white--text"
                            :disabled="true">
                            <div class="button-content">
                                <v-icon large>{{ customerIcon }}</v-icon>
                                <span class="container-btn-text">{{ $t('customerOperator') }}</span>
                                <span>{{ selectedCustomer || $t('selectCustomer') }}</span>
                            </div>
                        </v-btn>
                    </v-col>

                    <!-- Trucker Company Button -->
                    <v-col cols="12" md="4" class="rowBtnCol center">
                        <v-btn color="primary" size="x-large" width="100%" height="100%" class="white--text"
                            :disabled="isTruckerCompanyButtonDisabled" @click="selectTruckerCompanyButton">
                            <div class="button-content">
                                <v-icon large>{{ truckerCompanyIcon }}</v-icon>
                                <span class="container-btn-text">{{ $t('truckerCompany') }}</span>
                                <span>{{ selectedTruckerCompany || $t('selectTruckerCompany') }}</span>
                            </div>
                        </v-btn>
                    </v-col>

                    <!-- Licence Plate Button -->
                    <v-col cols="12" md="4" class="rowBtnCol center">
                        <v-btn color="primary" size="x-large" width="100%" height="100%" class="white--text"
                            :disabled="isLicencePlateButtonDisabled" @click="selectLicencePlate">
                            <div class="button-content">
                                <v-icon large>{{ licencePlateIcon }}</v-icon>
                                <span class="container-btn-text">{{ $t('licencePlate') }}</span>
                                <span>{{ selectedLicencePlate || $t('selectLicencePlate') }}</span>
                            </div>
                        </v-btn>
                    </v-col>
                </v-row>
            </div>

            <div>&nbsp;</div>

            <!-- Confirm Gate Out Button -->
            <div class="gate-out">
                <v-row>
                    <v-col class="rowBtnCol center">
                        <v-btn :disabled="isConfirmButtonDisabled" color="primary" size="large" width="100%"
                            height="100%" class="white--text" @click="confirmGateOut">
                            <span class="container-btn-text">{{ $t('confirmGateOut') }}</span>
                        </v-btn>
                    </v-col>
                </v-row>
            </div>
        </div>

        <div class="main-content-footer">
            <AppMessageNotifications />
            <v-toolbar :height="40">
                <v-btn icon="mdi-circle" size="small" color="light-green darken-1" variant="text"></v-btn>
                <v-btn size="x-small" variant="text" @click="reloadList">
                    <h2>Status: online</h2>
                </v-btn>
            </v-toolbar>
        </div>
    </div>

    <!-- Reference Popup Dialog -->
    <v-dialog v-model="referenceDialog" fullscreen>
        <v-card>
            <v-toolbar>
                <v-btn variant="text" @click="closeReferenceDialog">
                    <v-icon left>mdi-arrow-left</v-icon>
                    {{ $t('goBack') }}
                </v-btn>
                <v-toolbar-title class="text-center">
                    <div class="toolbar-title">
                        <h3>{{ $t('enterReference') }}</h3>
                    </div>
                </v-toolbar-title>
            </v-toolbar>
            <v-card-text>
                <v-text-field :label="$t('reference')" v-model="referenceInput" class="large-input" solo hide-details
                    full-width @keyup.enter="validateReference"></v-text-field>
                <v-btn color="primary" size="x-large" width="100%" height="50%" class="white--text"
                    @click="validateReference">
                    <span class="container-btn-text">{{ $t('submitReference') }}</span>
                </v-btn>
                <div v-if="referenceError" class="error">{{ referenceError }}</div>
            </v-card-text>
        </v-card>
    </v-dialog>

    <!-- Container Number Popup Dialog -->
    <v-dialog v-model="containerDialog" fullscreen>
        <v-card>
            <v-toolbar>
                <v-btn variant="text" @click="closeContainerDialog">
                    <v-icon left>mdi-arrow-left</v-icon>
                    {{ $t('goBack') }}
                </v-btn>
                <v-toolbar-title class="text-center">
                    <div class="toolbar-title">
                        <h3>{{ $t('enterContainerNumber') }}</h3>
                    </div>
                </v-toolbar-title>
            </v-toolbar>
            <v-card-text>
                <v-text-field :label="$t('containerNumber')" v-model="containerInput" class="large-input" solo
                    hide-details full-width maxlength="11" @keydown.enter="validateContainerNumber"></v-text-field>
                <v-btn color="primary" size="x-large" width="100%" height="50%" class="white--text"
                    @click="validateContainerNumber">
                    <span class="container-btn-text">{{ $t('submitContainer') }}</span>
                </v-btn>
                <v-alert type="info">
                    {{ $t('infoGateOutContainer') }}
                </v-alert>
                <div v-if="containerError" class="error">{{ containerError }}</div>
            </v-card-text>
        </v-card>
    </v-dialog>

    <!-- Trucker Company Popup Dialog -->
    <v-dialog v-model="truckerCompanyDialog" fullscreen>
        <!-- ... (Same as in the gate-in file, adjusted for gate-out) ... -->
        <v-card style="display: flex; flex-direction: column; height: 100vh;">
            <v-toolbar flat>
                <v-btn variant="text" @click="closeTruckerCompanyDialog">
                    <v-icon left>mdi-arrow-left</v-icon>
                    {{ $t('goBack') }}
                </v-btn>
                <v-toolbar-title class="text-center">
                    <div class="toolbar-title">
                        <h3>{{ $t('selectTruckerCompany') }}</h3>
                    </div>
                </v-toolbar-title>
            </v-toolbar>
            <!-- Main content area -->
            <v-card-text style="flex: 1; overflow-y: auto;">
                <!-- Input field for filtering Trucker Companies -->
                <v-text-field :label="$t('filterTruckerCompany')" v-model="truckerCompanyFilter" class="large-input"
                    solo hide-details full-width></v-text-field>
                <!-- List of filtered Trucker Companies -->
                <v-list>
                    <v-list-item v-for="(company, index) in filteredTruckerCompanies" :key="company.id + '-' + index"
                        @click="selectTruckerCompanyFromList(company)">
                        <v-list-item-content>
                            <v-list-item-title>
                                {{ company.name }}
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
                <!-- Error Message -->
                <div v-if="truckerCompanyError" class="error">{{ truckerCompanyError }}</div>
            </v-card-text>
            <!-- Add New Trucker Company Button -->
            <v-card-actions>
                <v-btn color="primary" size="large" width="100%" class="white--text"
                    @click="openAddTruckerCompanyDialog">
                    <span class="container-btn-text">{{ $t('addNewTruckerCompany') }}</span>
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- Add New Trucker Company Dialog -->
    <v-dialog v-model="addTruckerCompanyDialog" fullscreen>
        <!-- ... (Same as in the gate-in file) ... -->
        <v-card>
            <v-toolbar flat>
                <v-btn variant="text" @click="closeAddTruckerCompanyDialog">
                    <v-icon left>mdi-arrow-left</v-icon>
                    {{ $t('goBack') }}
                </v-btn>
                <v-toolbar-title class="text-center">
                    <div class="toolbar-title">
                        <h3>{{ $t('addNewTruckerCompany') }}</h3>
                    </div>
                </v-toolbar-title>
            </v-toolbar>
            <v-card-text>
                <!-- Input fields for name and email -->
                <v-text-field :label="$t('truckerCompanyName')" v-model="newTruckerCompanyName" class="large-input" solo
                    hide-details full-width></v-text-field>
                <v-text-field :label="$t('truckerCompanyEmail')" v-model="newTruckerCompanyEmail" class="large-input"
                    solo hide-details full-width type="email"></v-text-field>
                <!-- Submit Button -->
                <v-btn color="primary" size="large" width="100%" class="white--text" @click="submitNewTruckerCompany">
                    <span class="container-btn-text">{{ $t('submitTruckerCompany') }}</span>
                </v-btn>
                <!-- Error Message -->
                <div v-if="addTruckerCompanyError" class="error">{{ addTruckerCompanyError }}</div>
            </v-card-text>
        </v-card>
    </v-dialog>

    <!-- Licence Plate Popup Dialog -->
    <v-dialog v-model="licencePlateDialog" fullscreen>
        <!-- ... (Same as in the gate-in file) ... -->
        <v-card>
            <v-toolbar>
                <v-btn variant="text" @click="closeLicencePlateDialog">
                    <v-icon left>mdi-arrow-left</v-icon>
                    {{ $t('goBack') }}
                </v-btn>
                <v-toolbar-title class="text-center">
                    <div class="toolbar-title">
                        <h3>{{ $t('enterLicencePlate') }}</h3>
                    </div>
                </v-toolbar-title>
            </v-toolbar>
            <v-card-text>
                <v-text-field :label="$t('licencePlate')" v-model="licencePlateInput" class="large-input" solo
                    hide-details full-width @keyup.enter="submitLicencePlate"></v-text-field>
                <v-btn color="primary" size="x-large" width="100%" height="50%" class="white--text"
                    @click="submitLicencePlate">
                    <span class="container-btn-text">{{ $t('submitLicencePlate') }}</span>
                </v-btn>
                <!-- No validation needed, so no error message -->
            </v-card-text>
        </v-card>
    </v-dialog>

    <!-- Gate Out Confirmation Popup -->
    <v-dialog v-model="gateOutConfirmationDialog" persistent transition="slide-up-transition" width="300">
        <v-card class="confirmation-popup">
            <v-card-text class="text-center">
                <v-icon class="confirmation-icon" color="red" style="transform: scaleX(-1)">
                    mdi-truck-delivery
                </v-icon>
                <h2>{{ $t('gateOutConfirmed') }}</h2>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>


<script setup>
import { ref, inject, computed, onActivated, getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router';
import { useAppStore } from '@/stores/app';
import { useUtilities } from '@/composables/useUtilities';
import { useTransporter } from '@/composables/useTransporter';
import { useAdvancedAxios } from '@/composables/useAdvancedAxios';
import { useNotification } from '@/composables/useNotification';
import { useI18n } from 'vue-i18n';

// Use vue-i18n
const { t } = useI18n();

// INJECTIONS
const axios = inject('axios');
const { utilities } = useUtilities();

// VARIABLES
let position = ref('');
let containerId = ref(null);
let referenceId = ref(null);
let selectedReference = ref(null);
let selectedContainerNumber = ref(null);
let selectedContainerData = ref(null);
let selectedIsoCode = ref(null);
let selectedIsoCodeId = ref(null);
let selectedCustomer = ref(null);
let selectedCustomerId = ref(null);
let selectedTruckerCompany = ref(null);
let selectedTruckerCompanyId = ref(null);
let selectedLicencePlate = ref(null);

let newTruckerComment = ref(null);
let depotId = ref(null);

const inboundCarrierOptions = ref([]);

let hasContainersInReference = ref(false);

// For Add New Trucker Company Dialog
const addTruckerCompanyDialog = ref(false);
const newTruckerCompanyName = ref('');
const newTruckerCompanyEmail = ref('');
const addTruckerCompanyError = ref('');

// CONSTANTS
const store = useAppStore();
const componentId = getCurrentInstance().uid;
const router = useRouter();
const ready = ref(false);
const showFilter = ref(false);

// COMPOSABLES
const { transporter } = useTransporter();
const { advancedAxios } = useAdvancedAxios(axios);
const { notification } = useNotification();

// REACTIVE VARIABLES FOR DIALOGS
const referenceDialog = ref(false);
const referenceInput = ref('');
const referenceError = ref('');

const containerDialog = ref(false);
const containerInput = ref('');
const containerError = ref('');

const truckerCompanyDialog = ref(false);
const truckerCompanyFilter = ref('');
const truckerCompanyError = ref('');

const licencePlateDialog = ref(false);
const licencePlateInput = ref('');

const gateOutConfirmationDialog = ref(false);

// REACTIVE VARIABLE FOR PROCESSING STATE
const isProcessing = ref(false);

// COMPUTED PROPERTIES

const filteredTruckerCompanies = computed(() => {
    if (!truckerCompanyFilter.value) {
        return inboundCarrierOptions.value;
    }
    return inboundCarrierOptions.value.filter(company =>
        company.name.toLowerCase().includes(truckerCompanyFilter.value.toLowerCase())
    );
});

// Computed properties for button disabled states
const isReferenceButtonDisabled = computed(() => false); // Always enabled
const isContainerButtonDisabled = computed(() => !isReferenceSelected.value);
const isTruckerCompanyButtonDisabled = computed(() => !isReferenceSelected.value);
const isLicencePlateButtonDisabled = computed(() => !isTruckerCompanySelected.value);
const isConfirmButtonDisabled = computed(() => {
    return isProcessing.value || !(
        isReferenceSelected.value &&
        isTruckerCompanySelected.value &&
        isLicencePlateSelected.value
    );
});

const isReferenceSelected = computed(() => selectedReference.value !== null);
const isContainerSelected = computed(() => selectedContainerNumber.value !== null);
const isTruckerCompanySelected = computed(() => selectedTruckerCompany.value !== null);
const isLicencePlateSelected = computed(() => selectedLicencePlate.value !== null);

// Icons
const referenceIcon = computed(() => {
    return isReferenceSelected.value ? 'mdi-checkbox-marked-circle' : 'mdi-checkbox-blank-circle-outline';
});
const containerIcon = computed(() => {
    return isContainerSelected.value ? 'mdi-checkbox-marked-circle' : 'mdi-help-circle';
});
const isoCodeIcon = computed(() => 'mdi-checkbox-blank-circle-outline'); // Always disabled
const customerIcon = computed(() => 'mdi-checkbox-blank-circle-outline'); // Always disabled
const truckerCompanyIcon = computed(() => {
    return isTruckerCompanySelected.value ? 'mdi-checkbox-marked-circle' : 'mdi-checkbox-blank-circle-outline';
});
const licencePlateIcon = computed(() => {
    return isLicencePlateSelected.value ? 'mdi-checkbox-marked-circle' : 'mdi-checkbox-blank-circle-outline';
});

// EVENTS
onActivated(() => {
    ready.value = false;
    let transportData = transporter.getData(componentId);
    selectedContainerNumber.value = transportData ? transportData.unit_number : null;
    containerId.value = transportData ? transportData.id : null;
    selectedReference.value = transportData ? transportData.reference : null;
    referenceId.value = transportData ? transportData.ref_id : null;
    console.log(transportData);
    ready.value = true;
});

// METHODS

const confirmGateOut = async () => {
    if (!isConfirmButtonDisabled.value) {
        try {
            isProcessing.value = true;

            // Prepare data for creating the task
            const taskData = {
                container_id: containerId.value || null,
                reference_id: referenceId.value || null,
                depot_id: depotId.value || 552,
                type: 'release',
                status: 'open',
                gateInOutType: isContainerSelected.value ? 'numerical' : 'nonumerical',
                position: '',
                comment: 'Trucker Gate-Out.',
                licence_plate: selectedLicencePlate.value,
                carrier_id: selectedTruckerCompanyId.value || null,
            };

            // Create the task
            await addTask(taskData);

            // Show success message and confirmation popup
            store.addNotificationMessage(t('gateOutConfirmed'), 'success');
            gateOutConfirmationDialog.value = true;

            // Automatically close the popup after 2 seconds and reset selections
            setTimeout(() => {
                gateOutConfirmationDialog.value = false;
                resetSelections();
            }, 2000);

        } catch (error) {
            console.error('Error during gate-out confirmation:', error);
            const errorMessage = error.response?.data?.message || t('errorConfirmingGateOut');
            store.addNotificationMessage(errorMessage, 'error');
        } finally {
            isProcessing.value = false;
        }
    } else {
        store.addNotificationMessage(t('completeAllSelections'), 'error');
    }
};

const addTask = (taskData) => {
    return advancedAxios.sendRequest(taskData.container_id || 'nonumerical', { method: 'post', url: '/api/yamaxTasks', data: taskData })
        .then(function (response) {
            store.addNotificationMessage(t('taskAddedSuccessfully'), 'success');
        })
        .catch(function (error) {
            console.error('Error adding task:', error);
            store.addNotificationMessage(t('errorAddingTask'), 'error');
            throw error;
        });
};

const validateReference = async () => {
    try {
        // Clear previous errors
        referenceError.value = '';

        // Make a GET request to validate the reference
        const response = await axios.get('/references/check', {
            params: { reference: referenceInput.value.trim() },
        });

        const data = response.data;

        if (data.exists) {
            // Reference exists, populate the necessary fields
            selectedReference.value = referenceInput.value.trim();
            referenceId.value = data.reference.id;
            depotId.value = data.reference.depot_id;

            // Set ISO Code and Customer from reference (even though buttons are disabled)
            selectedIsoCode.value = data.reference.survey_size_type_id;
            selectedCustomer.value = data.reference.operator__company.name;

            // Fetch Trucker Companies and other necessary data
            await fetchAdditionalData();

            // Close the dialog
            closeReferenceDialog();
        } else {
            // Reference does not exist
            referenceError.value = t('referenceNotFound');
        }
    } catch (error) {
        console.error(error);
        referenceError.value = t('errorValidatingReference');
    }
};

const selectContainerButton = () => {
    containerDialog.value = true;
};

const closeContainerDialog = () => {
    containerDialog.value = false;
    containerInput.value = '';
    containerError.value = '';
};

const validateContainerNumber = async () => {
    const newValue = containerInput.value.toUpperCase().trim();

    // Validate container number format
    const containerRegex = /^[A-Z]{4}\d{7}$/;
    if (newValue.length === 11 && containerRegex.test(newValue)) {
        // Verify check digit
        const baseContainerId = newValue.substring(0, 10);
        const providedCheckDigit = newValue.charAt(10);
        const calculatedCheckDigit = utilities.generateUnitNumberCheckNumber(baseContainerId).toString();

        if (providedCheckDigit !== calculatedCheckDigit) {
            containerError.value = t('containerWrongCheckDigit', { containerNumber: newValue });
            store.addNotificationMessage(
                t('containerWrongCheckDigit', { containerNumber: `<b>${newValue}</b>` }),
                'warning'
            );
        } else {
            // Valid container number
            // Check if the container is in stock and exists
            try {
                const response = await axios.get('/stockContainer/check', {
                    params: { unit_number: newValue }
                });

                if (response.data && response.data.model && Object.keys(response.data.model).length > 0) {
                    // Container exists and is in stock
                    selectedContainerNumber.value = newValue;
                    containerId.value = response.data.model.id;

                    // Assign submodels to variables
                    inboundCarrierOptions.value = response.data.submodels.inbound_carrier_id;

                    // Close the dialog
                    closeContainerDialog();
                } else {
                    // Container does not exist or is not in stock
                    containerError.value = t('containerNotInStock');
                    store.addNotificationMessage(
                        t('containerNotInStock'),
                        'error'
                    );
                }
            } catch (error) {
                console.error(error);
                containerError.value = t('errorCheckingContainer');
                store.addNotificationMessage(
                    t('errorCheckingContainer'),
                    'error'
                );
            }
        }
    } else if (newValue.length === 10 && /^[A-Z]{4}\d{6}$/.test(newValue)) {
        // Append calculated check digit
        const checkDigit = utilities.generateUnitNumberCheckNumber(newValue).toString();
        const fullContainerId = newValue + checkDigit;

        // Check if the container is in stock and exists
        try {
            const response = await axios.get('/stockContainer/check', {
                params: { unit_number: fullContainerId }
            });

            if (response.data && response.data.model && Object.keys(response.data.model).length > 0) {
                // Container exists and is in stock
                selectedContainerNumber.value = fullContainerId;
                containerId.value = response.data.model.id;

                // Assign submodels to variables
                inboundCarrierOptions.value = response.data.submodels.inbound_carrier_id;

                // Close the dialog
                closeContainerDialog();
            } else {
                // Container does not exist or is not in stock
                containerError.value = t('containerNotInStock');
                store.addNotificationMessage(
                    t('containerNotInStock'),
                    'error'
                );
            }
        } catch (error) {
            console.error(error);
            containerError.value = t('errorCheckingContainer');
            store.addNotificationMessage(
                t('errorCheckingContainer'),
                'error'
            );
        }
    } else {
        containerError.value = t('invalidContainerFormat');
    }
};

const fetchAdditionalData = async () => {
    try {
        // Fetch inboundCarrierOptions (Trucker Companies)
        const response = await axios.get('/stockContainer/check', {
            params: { unit_number: "TESU1234567" }
        });
        inboundCarrierOptions.value = response.data.submodels.inbound_carrier_id;

        // Auto-select if only one Trucker Company is available
        if (inboundCarrierOptions.value.length === 1) {
            selectTruckerCompanyFromList(inboundCarrierOptions.value[0]);
        }
    } catch (error) {
        console.error('Error fetching additional data:', error);
        store.addNotificationMessage(
            t('errorFetchingData'),
            'error'
        );
    }
};

const selectTruckerCompanyButton = async () => {
    // If inboundCarrierOptions is empty, fetch it
    if (inboundCarrierOptions.value.length === 0) {
        await fetchAdditionalData();
    }

    if (inboundCarrierOptions.value.length === 1) {
        selectTruckerCompanyFromList(inboundCarrierOptions.value[0]);
    } else {
        truckerCompanyDialog.value = true;
    }
};

const closeTruckerCompanyDialog = () => {
    truckerCompanyDialog.value = false;
    truckerCompanyFilter.value = '';
    truckerCompanyError.value = '';
};

const selectTruckerCompanyFromList = (company) => {
    selectedTruckerCompany.value = company.name;
    selectedTruckerCompanyId.value = company.id;
    closeTruckerCompanyDialog();
};

const openAddTruckerCompanyDialog = () => {
    addTruckerCompanyDialog.value = true;
};

const closeAddTruckerCompanyDialog = () => {
    addTruckerCompanyDialog.value = false;
    newTruckerCompanyName.value = '';
    newTruckerCompanyEmail.value = '';
    addTruckerCompanyError.value = '';
};

const submitNewTruckerCompany = async () => {
    // Validate inputs
    if (!newTruckerCompanyName.value.trim()) {
        addTruckerCompanyError.value = t('truckerCompanyNameRequired');
        return;
    }
    if (!newTruckerCompanyEmail.value.trim()) {
        addTruckerCompanyError.value = t('truckerCompanyEmailRequired');
        return;
    }
    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newTruckerCompanyEmail.value.trim())) {
        addTruckerCompanyError.value = t('invalidEmail');
        return;
    }

    try {
        // Clear previous error
        addTruckerCompanyError.value = '';

        // Prepare data to send to backend
        const payload = {
            name: newTruckerCompanyName.value.trim(),
            email: newTruckerCompanyEmail.value.trim(),
        };

        // Send POST request to backend to create new Trucker Company
        const response = await axios.post('/truckerCompanies', payload);

        const newCompany = response.data;

        // Update the inboundCarrierOptions list
        inboundCarrierOptions.value.push(newCompany);

        // Select the new company
        selectTruckerCompanyFromList(newCompany);

    } catch (error) {
        console.error('Error adding Trucker Company:', error);
        addTruckerCompanyError.value = t('errorAddingTruckerCompany');
    }
};

const selectLicencePlate = () => {
    licencePlateDialog.value = true;
};

const closeLicencePlateDialog = () => {
    licencePlateDialog.value = false;
    licencePlateInput.value = '';
};

const submitLicencePlate = () => {
    selectedLicencePlate.value = licencePlateInput.value.trim();
    closeLicencePlateDialog();
};

const selectReferenceButton = () => {
    referenceDialog.value = true;
};

const closeReferenceDialog = () => {
    referenceDialog.value = false;
    referenceInput.value = '';
    referenceError.value = '';
};

const resetSelections = () => {
    selectedReference.value = null;
    referenceId.value = null;
    selectedContainerNumber.value = null;
    containerId.value = null;
    selectedContainerData.value = null;
    selectedIsoCode.value = null;
    selectedIsoCodeId.value = null;
    selectedCustomer.value = null;
    selectedCustomerId.value = null;
    selectedTruckerCompany.value = null;
    selectedTruckerCompanyId.value = null;
    selectedLicencePlate.value = null;
    newTruckerComment.value = null;
    depotId.value = null;
    hasContainersInReference.value = false;
};

const backButton = (params) => {
    if (showFilter.value == true) {
        toggleFilter();
    } else {
        router.push({ name: 'TgApp' });
    }
};

const toggleFilter = () => {
    showFilter.value = !showFilter.value;
};
</script>




<style scoped>
/* Global box-sizing reset */
*,
*::before,
*::after {
    box-sizing: border-box;
}

.main-content-area {
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
    /* Prevents unwanted scrollbars */
}

.main-content-playground {
    padding: 20px;
    display: flex;
    flex-direction: column;
}

.toolbar-title {
    font-family: "Roboto", sans-serif;
    font-weight: 500;
    letter-spacing: 1.2px;
    color: #ffffff;
}

.job-status {
    font-family: "Roboto", sans-serif;
    font-weight: 700;
    font-size: 0.9em;
    letter-spacing: 1px;
    color: #ffffff;
    margin-left: 8px;
}

.gate-out {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
}

.v-row {
    flex-grow: 1;
    display: flex;
}

.rowBtnCol {
    flex-grow: 1;
    display: flex;
}


.container-btn-text {
    font-weight: bold;
    text-align: center;
    text-transform: none;
}

.center {
    align-items: center;
    justify-content: center;
}

.error {
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 1.5em;
    text-align: center;
    color: red;
    margin-top: 1em;
}

.large-input {
    margin-bottom: 1.25em;
    /* 20px */
}

.large-input ::v-deep .v-label {
    font-size: 1.5em;
    /* 24px */
}

.large-input ::v-deep .v-input__slot {
    font-size: 1.5em;
    /* 24px */
    height: 3.75em;
    /* 60px */
}

.large-input ::v-deep input {
    font-size: 2em;
    /* 24px */
    height: 3.75em;
    /* 60px */
}

.large-input ::v-deep .v-field__field {
    padding: 1.25em 1em;
    /* 20px 16px */
}

.button-content {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.btn-icon {
    font-size: 5em;
}

.confirmation-popup {
    background-color: #e0f7fa;
    /* Light cyan background */
}

.confirmation-icon {
    font-size: 15em;
}

.confirmation-popup h2 {
    margin-top: 0.5em;
    font-size: 2em;
    font-weight: bold;
}

@media (max-width: 600px) {
    .container-btn-text {
        font-size: 1em;
    }
}

@media (min-width: 601px) {
    .container-btn-text {
        font-size: 2em;
    }
}
</style>